import {Component, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {BaseurlService} from '../../services/baseurl.service';
import {ChromeKrakenService, ChromeKrakenTabEntity, Page} from './chrome-kraken.service';
import {ToastrService} from 'ngx-toastr';
import {DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';

@Component({
    selector: 'app-chrome-kraken',
    imports: [
        DatePipe,
        FormsModule,
    ],
    templateUrl: './chrome-kraken.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './chrome-kraken.component.scss'
})
export class ChromeKrakenComponent implements OnInit, OnDestroy {


  sse: EventSource;
  tabs: ChromeKrakenTabEntity[] = [];

  searching = false;
  loading = false;
  devices: string[] = [];

  q = '';
  createdFrom = '';
  createdTo = '';
  updatedFrom = '';
  updatedTo = '';
  closedFrom = '';
  closedTo = '';
  device = '';
  includeIncognito = false;

  page = 0;
  size = 50;
  results: Page<ChromeKrakenTabEntity> = {
    content: [],
    totalPages: 0,
    totalElements: 0,
    last: true,
    size: 0,
    number: 0,
    sort: [],
    first: true,
    numberOfElements: 0,
  };

  private filterChange$ = new Subject<void>();
  private filterChangeSub?: Subscription;

  constructor(
    private zone: NgZone,
    private baseUrlService: BaseurlService,
    private chromeKrakenService: ChromeKrakenService,
    private notificationService: ToastrService,
  ) {
  }

  ngOnInit(): void {
    this.sse = new EventSource(this.baseUrlService.getBaseUrl() + '/chromeKraken/sse');
    this.sse.onmessage = (evt) => {
      console.info('received data', evt);

      this.zone.run(() => {
        if (!this.searching) {
          this.loadData();
        }
      });
    };
    this.loadData();

    this.chromeKrakenService.getDevices().subscribe({
      next: res => this.devices = res,
      error: err => this.notificationService.error('failed to read devices: ' + JSON.stringify(err)),
    });

    this.filterChangeSub = this.filterChange$.pipe(debounceTime(400)).subscribe(() => this.applyFilters());
  }

  private loadData() {
    this.chromeKrakenService.getTabs().subscribe({
      next: res => this.tabs = res,
      error: err => this.notificationService.error('failed to read data: ' + JSON.stringify(err)),
    });
  }

  onFilterChange(): void {
    this.filterChange$.next();
  }

  private hasActiveFilters(): boolean {
    return !!(this.q || this.createdFrom || this.createdTo || this.updatedFrom || this.updatedTo
      || this.closedFrom || this.closedTo || this.device || this.includeIncognito);
  }

  private applyFilters(): void {
    if (this.hasActiveFilters()) {
      this.searching = true;
      this.page = 0;
      this.runSearch();
    } else if (this.searching) {
      this.searching = false;
      this.loadData();
    }
  }

  search(): void {
    this.searching = true;
    this.page = 0;
    this.runSearch();
  }

  clearSearch(): void {
    this.searching = false;
    this.q = '';
    this.createdFrom = '';
    this.createdTo = '';
    this.updatedFrom = '';
    this.updatedTo = '';
    this.closedFrom = '';
    this.closedTo = '';
    this.device = '';
    this.includeIncognito = false;
    this.loadData();
  }

  nextPage(): void {
    if ((this.page + 1) * this.size < this.results.totalElements) {
      this.page++;
      this.runSearch();
    }
  }

  prevPage(): void {
    if (this.page > 0) {
      this.page--;
      this.runSearch();
    }
  }

  private runSearch(): void {
    this.loading = true;
    this.chromeKrakenService.searchTabs({
      q: this.q || null,
      createdFrom: this.toIsoOrNull(this.createdFrom),
      createdTo: this.toIsoOrNull(this.createdTo),
      updatedFrom: this.toIsoOrNull(this.updatedFrom),
      updatedTo: this.toIsoOrNull(this.updatedTo),
      closedFrom: this.toIsoOrNull(this.closedFrom),
      closedTo: this.toIsoOrNull(this.closedTo),
      device: this.device || null,
      includeIncognito: this.includeIncognito,
      page: this.page,
      size: this.size,
    }).subscribe({
      next: res => {
        this.results = res;
        this.loading = false;
      },
      error: err => {
        this.notificationService.error('search failed: ' + JSON.stringify(err));
        this.loading = false;
      },
    });
  }

  private toIsoOrNull(value: string): string | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.toISOString();
  }

  ngOnDestroy() {
    this.sse.close();
    this.filterChangeSub?.unsubscribe();
  }

}
